import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Box, Typography, Button, Select, MenuItem, FormControl, Link, Grid, CircularProgress, IconButton, Menu, MenuItem as MuiMenuItem, Divider } from '@mui/material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CloseIcon from '@mui/icons-material/Close';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { NotificationSnackbar } from '../../../common/components/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import { useNavItems } from '../../../hooks';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useCN54Report, type CN54ReportResponse, type MailDataStructure, type OperatorData, type MailItem } from './hooks';
import GroupedTable, { type ColumnGroup, type GroupedColumn } from '../../../common/components/Table/GroupedTable';
import type { SortConfig, SortDirection } from '../../../common/components/Table/types';
import { 
  MainContent,
  ReportContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton
} from './CN54.styled';

interface ServiceTypeState {
  aircraftPriority: boolean;
  salNonPriority: boolean;
  surfaceNonPriority: boolean;
  surfacePriority: boolean;
}

interface DespatchState {
  letters: boolean;
  flats: boolean;
  packets: boolean;
  pgCombined: boolean;
  pdfManual: boolean;
  journals: boolean;
  bilateral: boolean;
}

interface DirectionOption {
  id: string;
  label: string;
}

// Sorted GroupedTable wrapper component
const SortedGroupedTable: React.FC<{
  data: any[];
  columnGroups: ColumnGroup[];
  moduleName?: string;
  viewMode?: boolean;
  disableAutoSizing?: boolean;
  pageSize?: number;
  lazyLoad?: boolean;
  lazyLoadHeight?: number;
  lazyLoadingBatchSize?: number;
  rowBackgroundColor?: string | ((row: any) => string);
  summaryRows?: {
    subTotalRow?: any;
    weightProportionsAndIpkRow?: any;
  };
}> = ({ data, columnGroups, moduleName, viewMode, disableAutoSizing, pageSize, lazyLoad, lazyLoadHeight, lazyLoadingBatchSize, rowBackgroundColor, summaryRows }) => {
  const [sortConfig, setSortConfig] = useState<SortConfig | undefined>(undefined);
  const [sortAnchorEl, setSortAnchorEl] = useState<null | HTMLElement>(null);
  const [activeColumn, setActiveColumn] = useState<string>('');

  // Helper function to parse numeric values with thousand separators
  const parseNumeric = (value: unknown): number | null => {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string') {
      // Remove thousand separators (apostrophes, backticks, commas, spaces) and parse
      const cleaned = value.replace(/['`,\s]/g, '');
      const num = Number(cleaned);
      if (Number.isFinite(num)) return num;
    }
    return null;
  };

  const sortedData = useMemo(() => {
    if (!sortConfig) return data;

    const { column, direction: sortDirection } = sortConfig;
    const multiplier = sortDirection === 'ASC' ? 1 : -1;

    return [...data].sort((a, b) => {
      const aValue = a?.[column];
      const bValue = b?.[column];

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return -1 * multiplier;
      if (bValue == null) return 1 * multiplier;

      // Try numeric comparison first (handles thousand separators)
      const aNum = parseNumeric(aValue);
      const bNum = parseNumeric(bValue);
      
      if (aNum !== null && bNum !== null) {
        return (aNum - bNum) * multiplier;
      }

      // String comparison for non-numeric values
      const aStr = String(aValue);
      const bStr = String(bValue);
      return aStr.localeCompare(bStr, undefined, { numeric: true, sensitivity: 'base' }) * multiplier;
    });
  }, [data, sortConfig]);

  const handleSortClick = (event: React.MouseEvent<HTMLElement>, column: string) => {
    setSortAnchorEl(event.currentTarget);
    setActiveColumn(column);
  };

  const handleSortSelect = (direction: SortDirection) => {
    if (direction === sortConfig?.direction && sortConfig?.column === activeColumn) {
      // Clear sort if clicking the same direction
      setSortConfig(undefined);
    } else {
      setSortConfig({ column: activeColumn, direction });
    }
    setSortAnchorEl(null);
  };

  const handleCloseSortMenu = () => {
    setSortAnchorEl(null);
  };

  // Function to get sort icon for a column
  const getSortIcon = (column: string) => {
    if (sortConfig?.column === column) {
      return sortConfig.direction === 'ASC' ? <ArrowUpwardIcon sx={{ fontSize: 16 }} /> : <ArrowDownwardIcon sx={{ fontSize: 16 }} />;
    }
    return <MoreVertIcon sx={{ fontSize: 16 }} />;
  };

  // Create enhanced column groups with sortable headers
  const enhancedColumnGroups = useMemo(() => {
    return columnGroups.map(group => ({
      ...group,
      columns: group.columns.map(column => ({
        ...column,
        Header: (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
            {column.Header}
            <IconButton
              size="small"
              onClick={(e) => handleSortClick(e, column.accessor)}
              sx={{ p: 0.25 }}
            >
              {getSortIcon(column.accessor)}
            </IconButton>
          </Box>
        )
      }))
    }));
  }, [columnGroups, sortConfig]);

  return (
    <>
      <GroupedTable
        data={sortedData}
        columnGroups={enhancedColumnGroups}
        moduleName={moduleName}
        viewMode={viewMode}
        disableAutoSizing={disableAutoSizing}
        pageSize={pageSize}
        lazyLoad={lazyLoad}
        lazyLoadHeight={lazyLoadHeight}
        lazyLoadingBatchSize={lazyLoadingBatchSize}
        rowBackgroundColor={rowBackgroundColor}
        summaryRows={summaryRows}
      />
      
      {/* Sort Menu */}
      <Menu
        anchorEl={sortAnchorEl}
        open={Boolean(sortAnchorEl)}
        onClose={handleCloseSortMenu}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MuiMenuItem onClick={() => handleSortSelect('ASC')}>
          <ArrowUpwardIcon sx={{ mr: 1, fontSize: 16 }} />
          Sort Ascending
        </MuiMenuItem>
        <MuiMenuItem onClick={() => handleSortSelect('DESC')}>
          <ArrowDownwardIcon sx={{ mr: 1, fontSize: 16 }} />
          Sort Descending
        </MuiMenuItem>
        {sortConfig && sortConfig.column === activeColumn && (
          <>
            <Divider />
            <MuiMenuItem onClick={() => handleSortSelect(sortConfig.direction)}>
              <CloseIcon sx={{ mr: 1, fontSize: 16, color: '#F44336' }} />
              Clear Sort
            </MuiMenuItem>
          </>
        )}
      </Menu>
    </>
  );
};

// Create grouped column configuration for CN54
const createMailColumnGroups = (): ColumnGroup[] => [
  {
    id: 'basic',
    Header: '',
    columns: [
      { accessor: 'originOffice', Header: 'Origin Office' },
      { accessor: 'destinationOffice', Header: 'Destination Office' },
      { accessor: 'receptacleCategory', Header: 'Receptacle Category' },
    ]
  },
  {
    id: 'weight',
    Header: '',
    columns: [
      { accessor: 'sampledGrossWeight', Header: 'Sampled Gross Weight' },
      { accessor: 'sampledTareWeight', Header: 'Sampled Tare Weight' },
      { accessor: 'totalGrossWeight', Header: 'Total Gross Weight' },
    ]
  },
  {
    id: 'format-ps',
    Header: 'Format P/S',
    columns: [
      { accessor: 'psSampledNofItems', Header: 'Sampled No of Items' },
      { accessor: 'psSampledNetWeight', Header: 'Sampled Net Weight' },
      { accessor: 'psCalculatedSampledGrossWeight', Header: 'Calculated Sampled Gross Weight' },
      { accessor: 'psEstimatedGrossWeight', Header: 'Estimated Gross Weight' },
      { accessor: 'psEstimatedNofItems', Header: 'Estimated No of Items' },
    ]
  },
  {
    id: 'format-g',
    Header: 'Format G',
    columns: [
      { accessor: 'gsampledNofItems', Header: 'Sampled No of Items' },
      { accessor: 'gsampledNetWeight', Header: 'Sampled Net Weight' },
      { accessor: 'gcalculatedSampledGrossWeight', Header: 'Calculated Sampled Gross Weight' },
      { accessor: 'gestimatedGrossWeight', Header: 'Estimated Gross Weight' },
      { accessor: 'gestimatedNofItems', Header: 'Estimated No of Items' },
    ]
  },
  {
    id: 'format-e',
    Header: 'Format E',
    columns: [
      { accessor: 'esampledNofItems', Header: 'Sampled No of Items' },
      { accessor: 'esampledNetWeight', Header: 'Sampled Net Weight' },
      { accessor: 'ecalculatedSampledGrossWeight', Header: 'Calculated Sampled Gross Weight' },
      { accessor: 'eestimatedGrossWeight', Header: 'Estimated Gross Weight' },
      { accessor: 'eestimatedNofItems', Header: 'Estimated No of Items' },
    ]
  }
];

// Memoized Results Section Component
const ResultsSection = React.memo<{
  htmlResults: MailDataStructure;
  mailColumnGroups: ColumnGroup[];
  direction: string;
}>(({ htmlResults, mailColumnGroups, direction }) => {
  return (
    <Box sx={{ mt: 6 }}>
      {/* Main Results Title */}
      <Box sx={{ 
        borderBottom: '2px solid #E1E8ED',
        pb: 2,
        mb: 4
      }}>
        <Typography variant="h4" sx={{ 
          fontWeight: 700, 
          color: '#063142',
          fontSize: '24px',
          letterSpacing: '-0.5px'
        }}>
          Report Results
        </Typography>
        <Typography variant="body2" sx={{ 
          color: '#666',
          mt: 0.5,
          fontSize: '14px'
        }}>
          Generated CN54 report data tables
        </Typography>
      </Box>
      
      {htmlResults.map((operatorData: OperatorData, operatorIndex: number) => (
        <OperatorSection 
          key={`${operatorData.operator}-${operatorIndex}`}
          operatorData={operatorData}
          operatorIndex={operatorIndex}
          mailColumnGroups={mailColumnGroups}
          direction={direction}
        />
      ))}
    </Box>
  );
});

// Memoized Operator Section Component
const OperatorSection = React.memo<{
  operatorData: OperatorData;
  operatorIndex: number;
  mailColumnGroups: ColumnGroup[];
  direction: string;
}>(({ operatorData, operatorIndex, mailColumnGroups, direction }) => {
  return (
    <Box sx={{ 
      mb: 6,
      p: 3,
      border: '1px solid #E1E8ED',
      borderRadius: '8px',
      backgroundColor: '#FAFBFC'
    }}>
      {/* Operator Title */}
      <Box sx={{ 
        mb: 3,
        pb: 2,
        borderBottom: '1px solid #E1E8ED'
      }}>
        <Typography variant="h5" sx={{ 
          fontWeight: 600, 
          color: '#063142', 
          fontSize: '20px',
          letterSpacing: '-0.25px'
        }}>
          Operator: {operatorData.operator}
        </Typography>
      </Box>
      
      {operatorData.items.length === 0 ? (
        <Box sx={{ 
          p: 4,
          textAlign: 'center',
          backgroundColor: '#FFFFFF',
          borderRadius: '6px',
          border: '1px dashed #D1D9E0'
        }}>
          <Typography sx={{ 
            fontStyle: 'italic', 
            color: '#666',
            fontSize: '14px'
          }}>
            No data available for this operator
          </Typography>
        </Box>
      ) : (
        operatorData.items.map((item: MailItem, itemIndex: number) => (
          <MailItemSection
            key={`${item.key.fileName}-${item.key.mailCategory}-${item.key.mailFormat}-${itemIndex}`}
            item={item}
            itemIndex={itemIndex}
            isLastItem={itemIndex === operatorData.items.length - 1}
            mailColumnGroups={mailColumnGroups}
            direction={direction}
          />
        ))
      )}
    </Box>
  );
});

// Memoized Mail Item Section Component
const MailItemSection = React.memo<{
  item: MailItem;
  itemIndex: number;
  isLastItem: boolean;
  mailColumnGroups: ColumnGroup[];
  direction: string;
}>(({ item, itemIndex, isLastItem, mailColumnGroups, direction }) => {
  return (
    <Box sx={{ 
      mb: !isLastItem ? 4 : 0,
      p: 3,
      backgroundColor: '#FFFFFF',
      borderRadius: '6px',
      border: '1px solid #E1E8ED',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
    }}>
      {/* Table Header Info */}
      <Box sx={{ 
        mb: 3,
        p: 2,
        backgroundColor: '#F8F9FA',
        borderRadius: '4px',
        border: '1px solid #E9ECEF'
      }}>
        <Typography variant="h6" sx={{ 
          fontWeight: 600, 
          color: '#063142',
          fontSize: '16px',
          mb: 1
        }}>
          Mail Category: {item.key.mailCategory} | Mail Format: {item.key.mailFormat}
        </Typography>
        <Typography variant="body2" sx={{ 
          color: '#6C757D', 
          fontSize: '12px',
          fontFamily: 'monospace',
          backgroundColor: '#FFFFFF',
          padding: '4px 8px',
          borderRadius: '3px',
          border: '1px solid #DEE2E6',
          display: 'inline-block'
        }}>
          📄 {item.key.fileName}
        </Typography>
      </Box>
      
      {item.stratumRows.length === 0 ? (
        <Box sx={{ 
          p: 3,
          textAlign: 'center',
          backgroundColor: '#FFF8E1',
          borderRadius: '4px',
          border: '1px dashed #FFE082'
        }}>
          <Typography sx={{ 
            fontStyle: 'italic', 
            color: '#F57F17',
            fontSize: '14px',
            fontWeight: 500
          }}>
            ⚠️ No rows available for this item
          </Typography>
        </Box>
      ) : (
        <Box sx={{ 
          '& .MuiPaper-root': {
            boxShadow: 'none',
            border: '1px solid #E1E8ED'
          }
        }}>
          <SortedGroupedTable
            data={item.stratumRows}
            columnGroups={mailColumnGroups}
            moduleName={`${item.key.mailCategory}-${item.key.mailFormat}-${item.key.sectionType}`}
            viewMode={true}
            disableAutoSizing={false}
            pageSize={25}
            lazyLoad={item.stratumRows.length > 50}
            lazyLoadHeight={400}
            lazyLoadingBatchSize={25}
            rowBackgroundColor={direction === 'Inbound' ? 'rgb(230, 245, 230)' : 'rgb(255, 240, 240)'}
            summaryRows={{
              subTotalRow: item.subTotalRow,
              weightProportionsAndIpkRow: item.weightProportionsAndIpkRow
            }}
          />
        </Box>
      )}
    </Box>
  );
});

// Helper function to compare year-quarter combinations
const compareYearQuarter = (year1: string, quarter1: string, year2: string, quarter2: string): number => {
  const y1 = parseInt(year1);
  const y2 = parseInt(year2);
  const q1 = parseInt(quarter1.replace('Q', ''));
  const q2 = parseInt(quarter2.replace('Q', ''));
  
  if (y1 !== y2) {
    return y1 - y2; // Compare years first
  }
  return q1 - q2; // If years are equal, compare quarters
};

function CN54Report() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();
  
  const [ppoValue, setPpoValue] = useState(() => {
    const ppoParam = searchParams.get('ppo');
    return ppoParam ? (ppoParam === 'All' ? 'All' : ppoParam) : 'All';
  });
  const [ppoFilter, setPpoFilter] = useState(() => {
    const tableParam = searchParams.get('table');
    return tableParam === 'outbound' ? 'Outbound' : 'Inbound';
  });
  const [fromYear, setFromYear] = useState(() => {
    const fromYearParam = searchParams.get('fromYear');
    return fromYearParam || '';
  });
  const [fromQuarter, setFromQuarter] = useState(() => {
    const fromQuarterParam = searchParams.get('fromQuarter');
    return fromQuarterParam || '';
  });
  const [toYear, setToYear] = useState(() => {
    const toYearParam = searchParams.get('toYear');
    return toYearParam || '';
  });
  const [toQuarter, setToQuarter] = useState(() => {
    const toQuarterParam = searchParams.get('toQuarter');
    return toQuarterParam || '';
  });

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<MailDataStructure | null>(null);
  // Add state to track when no data is found
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  // Add a results key to force re-rendering when data changes
  const [resultsKey, setResultsKey] = useState<string>('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
  }>({
    open: false,
    message: '',
    severity: 'error'
  });

  // Get effective operators and cascading logic for e53 module
  const {
    operators,
    availableYears,
    operatorsForYearRange,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('e53', { fromYear, toYear, selectedPpo: ppoValue === 'All' ? undefined : ppoValue });

  // CN54 Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetReportError
  } = useCN54Report();

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = (fromYear || toYear) ? operatorsForYearRange : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYearRange, fromYear, toYear]);

  // Generate year-quarter options with cascading logic
  const yearQuarterOptions = React.useMemo(() => {
    const options: { value: string; label: string; year: string; quarter: string }[] = [];
    const quarters = ['Q1', 'Q2', 'Q3', 'Q4'];
    const yearsToShow = ppoValue && ppoValue !== 'All' ? yearsForOperator : availableYears;
    yearsToShow.forEach((year) => {
      quarters.forEach((quarter) => {
        const value = `${year}-${quarter}`;
        const label = `${year} ${quarter}`;
        options.push({ value, label, year: year.toString(), quarter });
      });
    });
    return options;
  }, [availableYears, yearsForOperator, ppoValue]);

  // Get current from and to values for display
  const getFromValue = () => {
    if (fromYear && fromQuarter) {
      // Ensure quarter is in the correct format (Q1, Q2, Q3, Q4)
      const quarter = fromQuarter.startsWith('Q') ? fromQuarter : `Q${fromQuarter}`;
      return `${fromYear}-${quarter}`;
    }
    return '';
  };

  const getToValue = () => {
    if (toYear && toQuarter) {
      // Ensure quarter is in the correct format (Q1, Q2, Q3, Q4)
      const quarter = toQuarter.startsWith('Q') ? toQuarter : `Q${toQuarter}`;
      return `${toYear}-${quarter}`;
    }
    return '';
  };

  // Handle from dropdown change
  const handleFromChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (fromYear !== option.year && ppoValue && ppoValue !== 'All') {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('All');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has e53 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'e53' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('All');
            setFromYear(option.year);
            setFromQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newFromYearNum = parseInt(option.year);
          
          // Check if the new from year is within the DO's range
          let shouldClearDO = false;
          
          if (toYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const toYearNum = parseInt(toYear);
            shouldClearDO = !(maxYear >= newFromYearNum && minYear <= toYearNum);
          } else {
            // Only from year is selected - check if DO has data from that year onwards
            shouldClearDO = maxYear < newFromYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('All');
          }
        }
        
        setFromYear(option.year);
        setFromQuarter(option.quarter);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
    }
  };

  // Handle to dropdown change
  const handleToChange = (value: string) => {
    if (value) {
      const option = yearQuarterOptions.find(opt => opt.value === value);
      if (option) {
        // Check if selected to date is earlier than current from date
        if (fromYear && fromQuarter) {
          const comparison = compareYearQuarter(fromYear, fromQuarter, option.year, option.quarter);
          if (comparison > 0) {
            showSnackbar('To date cannot be earlier than From date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        
        // Only clear DO selection if the currently selected DO is not available for the new year range
        if (toYear !== option.year && ppoValue && ppoValue !== 'All') {
          // Check if the current DO is available for the new year range
          const relationsForCurrentDO = allRelationsData?.byTargetDO?.[ppoValue];
          if (!relationsForCurrentDO) {
            setPpoValue('All');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          // Get years where this DO has e53 module enabled
          const availableYears = relationsForCurrentDO
            .filter(relation => 
              relation.modules.some(module => 
                module.name === 'e53' && module.enabled === true
              )
            )
            .map(relation => relation.year)
            .sort((a, b) => a - b);
          
          if (availableYears.length === 0) {
            setPpoValue('All');
            setToYear(option.year);
            setToQuarter(option.quarter);
            return;
          }
          
          const minYear = availableYears[0];
          const maxYear = availableYears[availableYears.length - 1];
          const newToYearNum = parseInt(option.year);
          
          // Check if the new to year is within the DO's range
          let shouldClearDO = false;
          
          if (fromYear) {
            // Both from and to are selected - check if the range overlaps with DO's range
            const fromYearNum = parseInt(fromYear);
            shouldClearDO = !(maxYear >= fromYearNum && minYear <= newToYearNum);
          } else {
            // Only to year is selected - check if DO has data up to that year
            shouldClearDO = minYear > newToYearNum;
          }
          
          if (shouldClearDO) {
            setPpoValue('All');
          }
        }
        
        setToYear(option.year);
        setToQuarter(option.quarter);
      }
    } else {
      setToYear('');
      setToQuarter('');
    }
  };

  // Service Type state
  const [serviceType, setServiceType] = useState<ServiceTypeState>({
    aircraftPriority: true,
    salNonPriority: true,
    surfaceNonPriority: true,
    surfacePriority: true,
  });

  // Despatch state
  const [despatch, setDespatch] = useState<DespatchState>({
    letters: true,
    flats: true,
    packets: true,
    pgCombined: true,
    pdfManual: true,
    journals: true,
    bilateral: true,
  });

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen(prev => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/e53');
  };

  const handleDirectionChange = (id: string) => {
    setPpoFilter(id);
  };

  // Handle DO selection change with cascading behavior
  const handleDOChange = (value: string) => {
    // Only clear year selection if the currently selected year range is not available for the new DO
    if (ppoValue !== value && (fromYear || toYear)) {
      // Check if the current year range is available for the new DO
      const relationsForCurrentDO = allRelationsData?.byTargetDO?.[value];
      if (!relationsForCurrentDO) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
        setPpoValue(value);
        return;
      }
      
      // Get years where this DO has e53 module enabled
      const availableYears = relationsForCurrentDO
        .filter(relation => 
          relation.modules.some(module => 
            module.name === 'e53' && module.enabled === true
          )
        )
        .map(relation => relation.year)
        .sort((a, b) => a - b);
      
      if (availableYears.length === 0) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
        setPpoValue(value);
        return;
      }
      
      const minYear = availableYears[0];
      const maxYear = availableYears[availableYears.length - 1];
      
      let shouldClearYears = false;
      
      if (fromYear && toYear) {
        // Both from and to are selected - check if the range overlaps with DO's range
        const fromYearNum = parseInt(fromYear);
        const toYearNum = parseInt(toYear);
        shouldClearYears = !(maxYear >= fromYearNum && minYear <= toYearNum);
      } else if (fromYear) {
        // Only from year is selected - check if DO has data from that year onwards
        const fromYearNum = parseInt(fromYear);
        shouldClearYears = maxYear < fromYearNum;
      } else if (toYear) {
        // Only to year is selected - check if DO has data up to that year
        const toYearNum = parseInt(toYear);
        shouldClearYears = minYear > toYearNum;
      }
      
      if (shouldClearYears) {
        setFromYear('');
        setFromQuarter('');
        setToYear('');
        setToQuarter('');
      }
    }
    setPpoValue(value);
  };

  const handleServiceTypeChange = (field: keyof ServiceTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setServiceType(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleDespatchChange = (field: keyof DespatchState) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setDespatch(prev => ({
      ...prev,
      [field]: event.target.checked
    }));
  };

  const handleSelectAllServiceType = () => {
    const allSelected = serviceType.aircraftPriority && 
                       serviceType.salNonPriority && 
                       serviceType.surfaceNonPriority && 
                       serviceType.surfacePriority;
    
    if (allSelected) {
      // Deselect all
      setServiceType({
        aircraftPriority: false,
        salNonPriority: false,
        surfaceNonPriority: false,
        surfacePriority: false,
      });
    } else {
      // Select all
      setServiceType({
        aircraftPriority: true,
        salNonPriority: true,
        surfaceNonPriority: true,
        surfacePriority: true,
      });
    }
  };

  const handleSelectAllDespatch = () => {
    const allSelected = despatch.letters && 
                       despatch.flats && 
                       despatch.packets && 
                       despatch.pgCombined &&
                       despatch.pdfManual && 
                       despatch.journals && 
                       despatch.bilateral;
    
    if (allSelected) {
      // Deselect all
      setDespatch({
        letters: false,
        flats: false,
        packets: false,
        pgCombined: false,
        pdfManual: false,
        journals: false,
        bilateral: false,
      });
    } else {
      // Select all
      setDespatch({
        letters: true,
        flats: true,
        packets: true,
        pgCombined: true,
        pdfManual: true,
        journals: true,
        bilateral: true,
      });
    }
  };

  const handleReset = () => {
    setPpoValue('All');
    setPpoFilter('Inbound');
    setFromYear('');
    setFromQuarter('');
    setToYear('');
    setToQuarter('');
    setServiceType({
      aircraftPriority: true,
      salNonPriority: true,
      surfaceNonPriority: true,
      surfacePriority: true,
    });
    setDespatch({
      letters: true,
      flats: true,
      packets: true,
      pgCombined: true,
      pdfManual: true,
      journals: true,
      bilateral: true,
    });
    // Clear no data found state and results when resetting
    setNoDataFound(false);
    setHtmlResults(null);
    setResultsKey('');
  };

  // Map checkbox states to API parameter values
  const getServiceTypeValues = (): string[] => {
    const values: string[] = [];
    if (serviceType.aircraftPriority) values.push('A');
    if (serviceType.salNonPriority) values.push('B');
    if (serviceType.surfaceNonPriority) values.push('C');
    if (serviceType.surfacePriority) values.push('D');
    return values;
  };

  const getMailFormatValues = (): string[] => {
    const values: string[] = [];
    if (despatch.letters) values.push('P');
    if (despatch.flats) values.push('G');
    if (despatch.packets) values.push('E');
    if (despatch.pgCombined) values.push('S');
    if (despatch.pdfManual) values.push('X');
    if (despatch.journals) values.push('J');
    if (despatch.bilateral) values.push('Z');
    return values;
  };

  // Function to apply current filters to SectionBar (URL params)
  const applyFiltersToSectionBar = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    if (ppoValue) {
      newParams.append('ppo', ppoValue);
    } else {
      newParams.append('ppo', 'ALL');
    }

    // Map direction filter to type
    newParams.append('type', ppoFilter.toLowerCase());

    // Add custom CN54Report-specific filters
    const serviceTypes = getServiceTypeValues();
    const mailFormats = getMailFormatValues();

    if (serviceTypes.length > 0) {
      newParams.append('serviceTypes', serviceTypes.join(','));
    }

    if (mailFormats.length > 0) {
      newParams.append('mailFormats', mailFormats.join(','));
    }

    if (fromYear && fromQuarter) {
      newParams.append('fromYear', fromYear);
      newParams.append('fromQuarter', fromQuarter);
      newParams.append('fromPeriod', `${fromYear}-${fromQuarter}`);
    }

    if (toYear && toQuarter) {
      newParams.append('toYear', toYear);
      newParams.append('toQuarter', toQuarter);
      newParams.append('toPeriod', `${toYear}-${toQuarter}`);
    }

    setSearchParams(newParams);
  };

  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors
    resetReportError();

    // Validate required fields
    const serviceTypes = getServiceTypeValues();
    const mailFormats = getMailFormatValues();

    if (serviceTypes.length === 0) {
      showSnackbar('Please select at least one Service Type');
      return;
    }

    if (mailFormats.length === 0) {
      showSnackbar('Please select at least one Despatch option');
      return;
    }

    if (!fromYear || !fromQuarter) {
      showSnackbar('Please select a From period');
      return;
    }

    if (!toYear || !toQuarter) {
      showSnackbar('Please select a To period');
      return;
    }

    // Validate date range
    const dateComparison = compareYearQuarter(fromYear, fromQuarter, toYear, toQuarter);
    if (dateComparison > 0) {
      showSnackbar('From date cannot be later than To date. Please select a valid date range.');
      return;
    }

    // Prepare API parameters
    const params = {
      serviceTypes,
      mailFormats,
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
      fromYear: parseInt(fromYear), 
      fromQuarter: parseInt(fromQuarter.replace('Q', '')),
      toYear: parseInt(toYear), 
      toQuarter: parseInt(toQuarter.replace('Q', '')),
      ...(ppoValue && ppoValue !== 'All' && { designatedOperator: ppoValue }),
      table: ppoFilter,
    };

    generateReport(params, {
      onSuccess: (response: CN54ReportResponse) => {
        if (format === 'HTML') {
          // For HTML, display the tables data received from the API
          console.log('HTML Report tables generated:', response.data);
          console.log('Current filter params used:', params);
          
          // Check for 204 No Content or empty data
          if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
            // Set no data found state instead of clearing results
            setNoDataFound(true);
            setHtmlResults(null);
            setResultsKey('no-data-' + Date.now());
            // Don't apply filters to SectionBar when no data is found
            return;
          }

          if (response.data) {
            // Store the data in a variable to ensure type safety
            const newData: MailDataStructure = response.data;
            // Clear no data found state and set new data
            setNoDataFound(false);
            setHtmlResults(null);
            // Use setTimeout to ensure the state is cleared before setting new data
            setTimeout(() => {
              setHtmlResults(newData);
              setResultsKey(`results-${Date.now()}`);
              console.log('Updated htmlResults state with new data:', newData);
            }, 0);
            // Apply current filters to SectionBar only when data is found
            applyFiltersToSectionBar();
          }
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(`${format} Report downloaded successfully with filename: ${response.filename}`);
        }
      },
      onError: async (error: Error) => {
        console.error('Error generating report:', error);
        
        // Handle NOT_FOUND errors for PDF/XLS exports
        if (format !== 'HTML') {
          let errorMessage = 'An error occurred while generating the report';
          let errorStatus: string | undefined;
          
          // Check if it's an axios error
          if (axios.isAxiosError(error)) {
            // For blob responses, the error data might be a Blob containing JSON
            if (error.response?.data instanceof Blob) {
              try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                errorMessage = errorData.message || errorMessage;
                errorStatus = errorData.status;
              } catch (parseError) {
                // If parsing fails, use default message
                console.error('Failed to parse error response:', parseError);
              }
            } else if (error.response?.data && typeof error.response.data === 'object') {
              // For non-blob responses, check the data directly
              const errorData = error.response.data as { message?: string; status?: string };
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            }
            
            // Check status code as fallback
            if (!errorStatus && error.response?.status === 404) {
              errorStatus = 'NOT_FOUND';
            }
          }
          
          // Show snackbar with appropriate message
          if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No CN54 reports available') || errorMessage.includes('No CN54')) {
            showSnackbar(
              errorMessage || 'No CN54 reports available for the selected criteria',
              'warning'
            );
          } else {
            showSnackbar(errorMessage, 'error');
          }
        } else {
          // For HTML format, show error snackbar
          showSnackbar(
            error instanceof Error ? error.message : 'Failed to generate report. Please try again.',
            'error'
          );
        }
      }
    });
  };

  // Memoize mailColumnGroups to prevent recreation on every render
  const mailColumnGroups = useMemo(() => createMailColumnGroups(), []);

  // Track if initial URL sync has been performed
  const [hasInitialUrlSync, setHasInitialUrlSync] = useState(false);

  // Initial URL parameter sync when page loads (only once)
  useEffect(() => {
    if (hasInitialUrlSync) return;
    
    const ppoParam = searchParams.get('ppo');
    const tableParam = searchParams.get('table');
    const typeParam = searchParams.get('type');
    const fromYearParam = searchParams.get('fromYear');
    const fromQuarterParam = searchParams.get('fromQuarter');
    const toYearParam = searchParams.get('toYear');
    const toQuarterParam = searchParams.get('toQuarter');

    // Sync PPO parameter
    if (ppoParam) {
      setPpoValue(ppoParam === 'All' ? 'All' : ppoParam);
    }

    // Sync direction parameter (check both 'type' and 'table' for backward compatibility)
    if (typeParam) {
      setPpoFilter(typeParam === 'outbound' ? 'Outbound' : 'Inbound');
    } else if (tableParam) {
      setPpoFilter(tableParam === 'outbound' ? 'Outbound' : 'Inbound');
    }

    // Sync from period parameters
    if (fromYearParam) {
      setFromYear(fromYearParam);
    }
    if (fromQuarterParam) {
      // Ensure quarter is stored in the correct format (Q1, Q2, Q3, Q4)
      const quarter = fromQuarterParam.startsWith('Q') ? fromQuarterParam : `Q${fromQuarterParam}`;
      setFromQuarter(quarter);
    }

    // Sync to period parameters
    if (toYearParam) {
      setToYear(toYearParam);
    }
    if (toQuarterParam) {
      // Ensure quarter is stored in the correct format (Q1, Q2, Q3, Q4)
      const quarter = toQuarterParam.startsWith('Q') ? toQuarterParam : `Q${toQuarterParam}`;
      setToQuarter(quarter);
    }
    
    setHasInitialUrlSync(true);
  }, [searchParams, hasInitialUrlSync]);

  // Sync URL params with page state when SectionBar filters change (only when results exist)
  // This is for syncing PPO and type (direction) changes from URL when results exist
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');
    const typeParam = searchParams.get('type');

    // Only sync if we have HTML results (filters are active)
    if (htmlResults) {
      if (ppoParam && ppoParam !== ppoValue) {
        setPpoValue(ppoParam === 'ALL' ? 'All' : ppoParam);
      }

      if (typeParam && typeParam !== ppoFilter.toLowerCase()) {
        const mappedFilter = typeParam === 'inbound' ? 'Inbound' :
                           typeParam === 'outbound' ? 'Outbound' : 'Inbound';
        setPpoFilter(mappedFilter);
      }
    }
  }, [searchParams, htmlResults, ppoValue, ppoFilter]);

  // Handle reportError through snackbar
  useEffect(() => {
    if (reportError) {
      showSnackbar(reportError.message || 'An error occurred while generating the report');
    }
  }, [reportError]);

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    setNoDataFound(false);
    setResultsKey('');
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Handle snackbar close
  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const showSnackbar = (message: string, severity: 'error' | 'warning' | 'info' | 'success' = 'error') => {
    setSnackbar({
      open: true,
      message,
      severity
    });
  };

  // Helper functions to determine button text
  const getServiceTypeButtonText = () => {
    const allSelected = serviceType.aircraftPriority && 
                       serviceType.salNonPriority && 
                       serviceType.surfaceNonPriority && 
                       serviceType.surfacePriority;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  const getDespatchButtonText = () => {
    const allSelected = despatch.letters && 
                       despatch.flats && 
                       despatch.packets && 
                       despatch.pgCombined &&
                       despatch.pdfManual && 
                       despatch.journals && 
                       despatch.bilateral;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  // Handler functions for SectionBar custom filter changes
  const handleSectionBarServiceTypesChange = useCallback((values: string[]) => {
    setServiceType({
      aircraftPriority: values.includes('A'),
      salNonPriority: values.includes('B'),
      surfaceNonPriority: values.includes('C'),
      surfacePriority: values.includes('D'),
    });
  }, []);

  const handleSectionBarMailFormatsChange = useCallback((values: string[]) => {
    setDespatch({
      letters: values.includes('P'),
      flats: values.includes('G'),
      packets: values.includes('E'),
      pgCombined: values.includes('S'),
      pdfManual: values.includes('X'),
      journals: values.includes('J'),
      bilateral: values.includes('Z'),
    });
  }, []);

  const handleSectionBarFromPeriodChange = useCallback((period: string) => {
    if (period) {
      const option = yearQuarterOptions.find(opt => opt.value === period);
      if (option) {
        // Check if selected from date is later than current to date
        if (toYear && toQuarter) {
          const comparison = compareYearQuarter(option.year, option.quarter, toYear, toQuarter);
          if (comparison > 0) {
            showSnackbar('From date cannot be later than To date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        setFromYear(option.year);
        setFromQuarter(option.quarter);
        
        // Update URL parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('fromYear');
        newParams.delete('fromQuarter');
        newParams.append('fromYear', option.year);
        newParams.append('fromQuarter', option.quarter);
        setSearchParams(newParams);
      }
    } else {
      setFromYear('');
      setFromQuarter('');
      
      // Update URL parameters to clear from period
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('fromYear');
      newParams.delete('fromQuarter');
      setSearchParams(newParams);
    }
  }, [yearQuarterOptions, toYear, toQuarter, searchParams, setSearchParams, showSnackbar]);

  const handleSectionBarToPeriodChange = useCallback((period: string) => {
    if (period) {
      const option = yearQuarterOptions.find(opt => opt.value === period);
      if (option) {
        // Check if selected to date is earlier than current from date
        if (fromYear && fromQuarter) {
          const comparison = compareYearQuarter(fromYear, fromQuarter, option.year, option.quarter);
          if (comparison > 0) {
            showSnackbar('To date cannot be earlier than From date. Please select a valid date range.', 'warning');
            return; // Don't update the state if invalid
          }
        }
        setToYear(option.year);
        setToQuarter(option.quarter);
        
        // Update URL parameters
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('toYear');
        newParams.delete('toQuarter');
        newParams.append('toYear', option.year);
        newParams.append('toQuarter', option.quarter);
        setSearchParams(newParams);
      }
    } else {
      setToYear('');
      setToQuarter('');
      
      // Update URL parameters to clear to period
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('toYear');
      newParams.delete('toQuarter');
      setSearchParams(newParams);
    }
  }, [yearQuarterOptions, fromYear, fromQuarter, searchParams, setSearchParams, showSnackbar]);

  // Handler for SectionBar direction change
  const handleSectionBarDirectionChange = useCallback((direction: string) => {
    setPpoFilter(direction);
    
    // Update URL parameters
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('type');
    newParams.append('type', direction.toLowerCase());
    setSearchParams(newParams);
  }, [searchParams, setSearchParams]);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    // Only show SectionBar custom filters when we have actual results (not when no data found)
    if (!htmlResults || noDataFound) return undefined;

    return {
      serviceTypes: getServiceTypeValues(),
      mailFormats: getMailFormatValues(),
      fromPeriod: getFromValue(),
      toPeriod: getToValue(),
      direction: ppoFilter,
      onServiceTypesChange: handleSectionBarServiceTypesChange,
      onMailFormatsChange: handleSectionBarMailFormatsChange,
      onFromPeriodChange: handleSectionBarFromPeriodChange,
      onToPeriodChange: handleSectionBarToPeriodChange,
      onDirectionChange: handleSectionBarDirectionChange,
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    noDataFound,
    serviceType,
    despatch,
    fromYear,
    fromQuarter,
    toYear,
    toQuarter,
    ppoFilter,
    getFromValue,
    getToValue,
    handleSectionBarServiceTypesChange,
    handleSectionBarMailFormatsChange,
    handleSectionBarFromPeriodChange,
    handleSectionBarToPeriodChange,
    handleSectionBarDirectionChange,
    handleExport,
    isGeneratingReport,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='cn54-report-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='CN54 Report'
            subtitle='e53 Sampling'
            module="e53"
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            onSubtitleClick={handleSubtitleClick}
            showFilters={{
              ppo: !!htmlResults && !noDataFound,
              type: !!htmlResults && !noDataFound,
              customCN54: !!htmlResults && !noDataFound,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid="section-bar"
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="cn54-report-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='cn54-report-main-content'>
            <ReportContainer data-testid="cn54-report-content">
              {/* Header */}
              <Box sx={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'center', 
                mb: 3 
              }} data-testid="cn54-report-header">
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#063142' }} data-testid="cn54-report-title">
                  CN54 Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="cn54-report-header-actions">
                  {(htmlResults || noDataFound) && (
                    <ClearResultsButton 
                      variant="outlined" 
                      onClick={clearResults}
                      data-testid="cn54-report-clear-results-btn"
                    >
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>

              {/* Main Filter Section - Hide when HTML results exist (but show when no data found) */}
              {!htmlResults && (
                <>
                  {/* PPO Section */}
                  <FilterSection data-testid="cn54-report-filters">
                    {/* PPO Dropdown and Direction Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }} data-testid="cn54-report-ppo-direction-section">
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }} data-testid="cn54-report-ppo-section">
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                              data-testid="cn54-report-ppo-select"
                            >
                              <MenuItem value="All">All</MenuItem>
                              {isPpoLoading && (
                                <MenuItem value="" disabled>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                </MenuItem>
                              )}
                              {!isPpoLoading && !ppoError && ppoOptions.map((option) => (
                                <MenuItem key={option.id} value={option.id}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
                                    <span>{option.label}</span>
                                  </Box>
                                </MenuItem>
                              ))}
                              {ppoError && (
                                <MenuItem disabled>Error loading PPOs</MenuItem>
                              )}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      {/* Direction Filter using Segmented Control */}
                      <StyledSegmentedControl data-testid="cn54-report-direction-filter">
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ppoFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{ 
                              color: ppoFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px'
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      mb: 2 
                    }} />

                    {/* Date Selectors with titles */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                        From YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth required>
                            <Select
                              value={getFromValue()}
                              onChange={(e) => handleFromChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value="">Select</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                      
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          To YYYY.QQ: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size="small" fullWidth required>
                            <Select
                              value={getToValue()}
                              onChange={(e) => handleToChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value="">Select</MenuItem>
                              {yearQuarterOptions.map((option) => (
                                <MenuItem key={option.value} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>
                  </FilterSection>

                  <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      mb: 2 
                    }} />

                  {/* Main Content Grid - without borders, with dividers */}
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: { xs: 'column', md: 'row' },
                    width: '100%'
                  }}>
                    {/* Service Type */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <SectionTitle>Service Type</SectionTitle>
                        <Link 
                          component="button"
                          onClick={handleSelectAllServiceType}
                          sx={{ 
                            color: '#00ACE7', 
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none'
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC'
                            }
                          }}
                        >
                          {getServiceTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={serviceType.aircraftPriority}
                          onChange={handleServiceTypeChange('aircraftPriority')}
                          label="A - Airmail Priority"
                        />
                        <CustomCheckbox
                          checked={serviceType.salNonPriority}
                          onChange={handleServiceTypeChange('salNonPriority')}
                          label="B - SAL Non-Priority"
                        />
                        <CustomCheckbox
                          checked={serviceType.surfaceNonPriority}
                          onChange={handleServiceTypeChange('surfaceNonPriority')}
                          label="C - Surface Non-Priority"
                        />
                        <CustomCheckbox
                          checked={serviceType.surfacePriority}
                          onChange={handleServiceTypeChange('surfacePriority')}
                          label="D - Surface Priority"
                        />
                      </CheckboxGroup>
                    </Box>

                    {/* Vertical Divider */}
                    <Box sx={{ 
                      width: '1px', 
                      backgroundColor: '#E1E8ED',
                      display: { xs: 'none', md: 'block' }
                    }} />

                    {/* Horizontal Divider for mobile */}
                    <Box sx={{ 
                      height: '1px', 
                      backgroundColor: '#E1E8ED', 
                      my: 2,
                      display: { xs: 'block', md: 'none' }
                    }} />

                    {/* Despatch */}
                    <Box sx={{ 
                      flex: { xs: 'none', md: '1 1 0' },
                      padding: 2,
                      minWidth: 0
                    }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
                        <SectionTitle>Mail Format</SectionTitle>
                        <Link 
                          component="button"
                          onClick={handleSelectAllDespatch}
                          sx={{ 
                            color: '#00ACE7', 
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none'
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC'
                            }
                          }}
                        >
                          {getDespatchButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={despatch.letters}
                          onChange={handleDespatchChange('letters')}
                          label="P - Letters"
                        />
                        <CustomCheckbox
                          checked={despatch.flats}
                          onChange={handleDespatchChange('flats')}
                          label="G - Flats"
                        />
                        <CustomCheckbox
                          checked={despatch.packets}
                          onChange={handleDespatchChange('packets')}
                          label="E - Packets"
                        />
                        <CustomCheckbox
                          checked={despatch.pgCombined}
                          onChange={handleDespatchChange('pgCombined')}
                          label="S - P/G combined"
                        />
                        <CustomCheckbox
                          checked={despatch.pdfManual}
                          onChange={handleDespatchChange('pdfManual')}
                          label="X - PGE Mixed"
                        />
                        <CustomCheckbox
                          checked={despatch.journals}
                          onChange={handleDespatchChange('journals')}
                          label="J - Journals"
                        />
                        <CustomCheckbox
                          checked={despatch.bilateral}
                          onChange={handleDespatchChange('bilateral')}
                          label="Z - Bilaterals"
                        />
                      </CheckboxGroup>
                    </Box>


                  </Box>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'flex-end' }}>
                    <ResetButton 
                      variant="contained" 
                      onClick={handleReset}
                    >
                      RESET
                    </ResetButton>
                    <ExportButton 
                      variant="contained" 
                      onClick={() => handleExport('HTML')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                    </ExportButton>
                    <ExportButton 
                      variant="contained" 
                      onClick={() => handleExport('PDF')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'PDF'}
                    </ExportButton>
                    <ExportButton 
                      variant="contained" 
                      onClick={() => handleExport('XLS')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                    </ExportButton>
                  </ButtonContainer>
                </>
              )}

              {/* No Data Found Section */}
              {noDataFound && (
                <NoDataFound
                  title="No Data Available"
                  message="No CN54 report data was found for the selected criteria. Please try adjusting your filters or selecting a different date range."
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                />
              )}

              {/* Results Section - Show tables when data exists */}
              {htmlResults && !noDataFound && (
                <ResultsSection 
                  key={resultsKey} 
                  htmlResults={htmlResults} 
                  mailColumnGroups={mailColumnGroups} 
                  direction={ppoFilter}
                />
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Snackbar for notifications */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid="cn54-notification"
      />
    </Box>
  );
}

export default CN54Report; 